import { AWAKEN, FEAT, FEAT_BOSSES, FEAT_GUARDIANS, FEATS, MAREAS_PER_CAMPAIGN, UNIQUES, featGoal, legendUses, type GearAttr, type UniqueId } from '@somnhold/content';
import { featTests } from '../battle/feats.ts';
import { imax, imin } from '../math/fixed.ts';
import type { BattleState } from '../types.ts';
import { event } from './core.ts';
import { refreshUnique, wornItems } from './items.ts';
import type { CampaignState, HeroFeat, HeroState, ItemInstance, Ms } from './types.ts';

/**
 * The feats of the unique pieces, on the campaign's side (docs/UNICAS.md §2): after each fight, what each hero did with
 * its pieces' power (battle/feats.ts) moves its feats on. One at a time; done, the next starts counting at once, even
 * before the stage is awakened at the forge.
 */

/**
 * Where a fight was, for the feats (§2.2, §2.3):
 * - `skip`: counts for nothing (Brann and Hilde's bets);
 * - `legend`: counts only for the Legend (a finished chapter played again, `replay`/`rerun`);
 * - `hard`: one of the hard places (the deep crypt, the Furrows, the late Mareas, a hard or heroic saga chapter from
 *   group 21): the test counts for En lo hondo too;
 * - `sima`: the Chasm's floor reached (hard, and its guardians count for the Legend, from a third of the hero's level);
 * - `deepMarea`: a late Marea (its bosses count for the Legend).
 */
export interface FeatPlace {
  skip?: boolean;
  legend?: boolean;
  hard?: boolean;
  sima?: number;
  deepMarea?: boolean;
}

/** Whether a Marea is one of the late ones (from the seventh): hard, and its bosses count. */
export const deepMarea = (marea: number): boolean => marea >= MAREAS_PER_CAMPAIGN;

/** The Chasm counts from the floor a third of the hero's level (it doesn't scale with the level: the floor is the difficulty). */
const simaCounts = (floor: number | undefined, level: number): boolean => floor !== undefined && floor >= Math.trunc(level / 3);

/** Whether a big boss felled here counts for this hero's Legend. */
const bossCounts = (felled: string, place: FeatPlace, level: number): boolean => {
  // In the Chasm a boss carries the floor it fell on.
  const [key = '', at] = felled.split('@');
  const floor = at !== undefined ? Number(at) : place.sima;
  if (key.startsWith('un:')) return true;
  if (key === 'witch' || key === 'shell') return !!place.deepMarea;
  if (FEAT_BOSSES.includes(key as never)) return true;
  return FEAT_GUARDIANS.includes(key as never) && simaCounts(floor, level);
};

/** A piece's feat, as the hero has it (null: no piece, or none left). */
export const featOf = (h: HeroState, id: UniqueId): HeroFeat | null => {
  const f = h.feats?.[id];
  return f && f.step <= 4 ? f : null;
};

/** The players of the campaign (not those who left): the allies' tests ask at most them less one. */
const groupSize = (s: CampaignState): number => s.players.filter((p) => !p.left).length;

/** How many allies a piece's test asks in this campaign (§2.2): its own number, at most the group less one, at least one. */
export const featAllies = (s: CampaignState, id: UniqueId): number => {
  const want = FEATS[id].allies ?? 0;
  return want ? imax(1, imin(want, groupSize(s) - 1)) : 0;
};

/** What a spectre added to a piece's feat in a fight (the wave's damage report tells its player). */
export interface FeatGain {
  pid: string;
  unique: UniqueId;
  step: number;
  n: number;
}

/**
 * Adds a fight to the feats of every hero in it who wears unique pieces: live and spectres, never mercenaries or the cast
 * (they aren't players of the campaign). Returns what the spectres added, for the report.
 */
export const tallyFeats = (s: CampaignState, battle: BattleState, place: FeatPlace, now: Ms): FeatGain[] => {
  const gains: FeatGain[] = [];
  if (place.skip) return gains;
  const all = battle.stats.feats;
  const felled = battle.stats.felled ?? [];
  if (!all && !felled.length) return gains;
  const solo = groupSize(s) === 1;
  // Spectres the dragon of the Burn ate were out of the fight from then on: what fell after isn't theirs (§4.4).
  const eaten = battle.dragon?.eaten ?? [];
  for (const bh of battle.heroes) {
    if (bh.control === 'merc' || !bh.uq?.length) continue;
    const p = s.players.find((x) => x.id === bh.pid);
    if (!p) continue;
    const h = p.hero;
    for (const id of bh.uq) {
      const f = featOf(h, id);
      if (!f) continue;
      const t = all?.[bh.pid]?.[id];
      const step = f.step;
      let add = 0;
      if (step === 4) {
        // The Legend: a big boss felled with the piece on and its power used enough in the fight.
        if (!t || t.p < legendUses(id) || eaten.includes(bh.pid)) continue;
        const bosses = (f.bosses ??= []);
        for (const f of felled) {
          const key = f.split('@')[0] ?? f;
          if (bossCounts(f, place, h.level) && !bosses.includes(key)) bosses.push(key);
        }
        add = bosses.length - f.n;
        f.n = bosses.length;
      } else {
        if (!t || place.legend) continue;
        if (step === 1) add = t.u;
        else if (step === 2 || ((place.hard || simaCounts(place.sima, h.level)) && step === 3)) add = featTests(id, t, featAllies(s, id), solo);
        f.n += add;
      }
      if (add <= 0) continue;
      if (bh.control === 'ghost') gains.push({ pid: p.id, unique: id, step, n: add });
      if (f.n >= featGoal(id, step)) {
        f.step = step + 1;
        f.n = 0;
        event(s, now, 'feat', { pid: p.id, data: { unique: id, step, done: true } });
      }
    }
  }
  return gains;
};

/* ---------- campaigns from before (2026-11-02) ---------- */

/** The old prize of the Turtle chapters that stopped giving a unique piece, for a chapter already begun (§5). */
const OLD_PAYS: Record<number, { unique: 'armor' | 'amulet'; level: number }> = { 3: { unique: 'armor', level: 10 }, 5: { unique: 'amulet', level: 15 } };

/** A hero's unique pieces moved onto their paths: their own attributes kept apart, their feats started. */
const heroPaths = (h: HeroState): boolean => {
  let changed = false;
  for (const it of [...wornItems(h), ...h.bag] as ItemInstance[]) {
    if (!it.unique) continue;
    if (!it.uqBase) {
      // What it came with: its attributes less the stages it woke (awakenItem added them to every one).
      const base: Partial<Record<GearAttr, number>> = {};
      for (const k of Object.keys(it.attrs).sort() as GearAttr[]) base[k] = (it.attrs[k] ?? 0) - (it.awaken ?? 0) * AWAKEN.attrStepBp;
      it.uqBase = base;
      refreshUnique(it, h.level);
      changed = true;
    }
    const feats = (h.feats ??= {});
    if (!feats[it.unique]) {
      const stage = it.awaken ?? 0;
      const f: HeroFeat = { step: stage + 1, n: 0 };
      // Still without its stage I: its first feat starts along by the levels gained since it came (÷ 10, up to all of it).
      if (stage === 0) {
        const goal = featGoal(it.unique, 1);
        const gained = imax(0, h.level - FEAT.oldLevel[UNIQUES[it.unique].slot]);
        const n = Math.trunc((goal * imin(10000, gained * FEAT.catchUp)) / 10000);
        if (n >= goal) f.step = 2;
        else f.n = n;
      }
      feats[it.unique] = f;
      changed = true;
    }
  }
  return changed;
};

/**
 * Campaigns from before the unique pieces' paths (docs/UNICAS.md §2.4, §5, §7): every piece keeps its own attributes
 * apart and follows its hero's level, starts its feats (at the one after its stage; the first ones along by the levels
 * the hero gained), and a Turtle chapter already begun that paid a piece still pays it. Idempotent; runs when a campaign loads.
 */
export const ensureUniquePaths = (s: CampaignState): boolean => {
  let changed = false;
  for (const p of s.players) {
    if (heroPaths(p.hero)) changed = true;
    for (const b of p.bench ?? []) if (heroPaths(b.hero)) changed = true;
  }
  if (s.uniquePaths === undefined) {
    s.uniquePaths = 1;
    const sg = s.saga;
    const begun = !!sg && (sg.act > 0 || sg.played.length > 0 || !!sg.vote);
    const old = sg ? OLD_PAYS[sg.chapter] : undefined;
    if (sg && begun && old && !sg.pays) sg.pays = { chapter: sg.chapter, ...old };
    changed = true;
  }
  return changed;
};
